import React, { useContext } from 'react';
import {
  AgxButton,
  AgxBodyText,
  Images,
  AgxColumn,
  AgxRow,
  AgxRadio,
  PoolDocumentation,
  PoolDocumentationType,
  useWindowSize,
  Breakpoints,
  PropertyType,
} from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { AuthContext } from '@urbanx/agx.auth.client';
import useFormPropertyDetailsSelector from 'selectors/useFormPropertyDetailsSelector';
import { LoadingState } from 'utils/loadingState';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { setAndShowErrorToast } from 'store/config';
import { campaignsApi } from 'Api/Campaigns/campaignsApi';
import AlertIcon from './assets/icons/alert-triangleOutline.svg?react';
import CheckmarkIcon from './assets/icons/checkmark-circleOutline.svg?react';
import './PoolSafetyCertificate.scss';
import { FormType } from '@urbanx/agx.campaigns.client';

interface PoolSafetyCertificateProps {
  id: string;
  onValueChanged: (value: { id: string; value?: PoolDocumentation | null }) => void;
  defaultValue?: PoolDocumentation | null;
  required: boolean;
  validate: boolean;
}

const expiryWarningThreshold = 60;

const PoolSafetyCertificate = ({
  id,
  onValueChanged,
  defaultValue,
  required,
  validate,
}: PoolSafetyCertificateProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const windowSize = useWindowSize();
  const { selectedForm } = useAppSelector((state) => state.form);
  const {
    loadingState: poolCertLoadingState,
    poolSafetyCertificate,
    buildingCertificate,
    poolSafetyCertificateDaysUntilExpiry,
    buildingCertificateDaysUntilExpiry,
  } = useAppSelector((state) => state.poolDocumentation);
  const { propertyType } = useFormPropertyDetailsSelector();

  const poolDocumentationType = defaultValue?.poolDocumentationType;
  const hasPool = !!poolDocumentationType;

  const dispatch = useAppDispatch();

  const showNonSharedLabel = [
    PropertyType.UnitApartment,
    PropertyType.Townhouse,
    PropertyType.Villa,
    PropertyType.Retirement,
  ].includes(propertyType as PropertyType);

  const poolLabel = showNonSharedLabel ? 'Has non-shared pool/spa' : 'Has Pool or Spa';

  const certificateExpiresInDays =
    poolDocumentationType === PoolDocumentationType.PoolSafetyCertificate
      ? poolSafetyCertificateDaysUntilExpiry
      : poolDocumentationType === PoolDocumentationType.BuildingCertificate
        ? buildingCertificateDaysUntilExpiry
        : undefined;

  const onHasPoolOptionChange = (hasPool: boolean) => {
    let nextPoolDocumentationType: PoolDocumentationType | undefined;

    if (hasPool) {
      const hasPoolCertificate = !!(poolSafetyCertificate ?? defaultValue?.poolSafetyCertificate);
      const hasBuildingCertificate = !!(buildingCertificate ?? defaultValue?.buildingCertificate);

      if (hasBuildingCertificate && !hasPoolCertificate) {
        nextPoolDocumentationType = PoolDocumentationType.BuildingCertificate;
      } else if (hasPoolCertificate) {
        nextPoolDocumentationType = PoolDocumentationType.PoolSafetyCertificate;
      } else {
        nextPoolDocumentationType = PoolDocumentationType.NoPoolCertificate;
      }
    }

    const poolDocumentation = {
      poolDocumentationType: nextPoolDocumentationType,
      poolSafetyCertificate: poolSafetyCertificate ?? defaultValue?.poolSafetyCertificate,
      buildingCertificate:
        nextPoolDocumentationType === PoolDocumentationType.BuildingCertificate
          ? {
              expiryDateExclusive: (buildingCertificate ?? defaultValue?.buildingCertificate)
                ?.expiryDateExclusive,
              document: defaultValue?.buildingCertificate?.document,
            }
          : undefined,
      noPoolCertificateInfo: defaultValue?.noticeOfNoPoolCertificate,
    } as PoolDocumentation;

    onValueChanged({ id, value: poolDocumentation });
  };

  const downloadDocument = async () => {
    if (!getAuthToken || !defaultValue?.poolSafetyCertificate) return;

    const authToken = await getAuthToken();

    if (!authToken) return;

    const { containerFilePath } = defaultValue?.poolSafetyCertificate?.document ?? {};

    if (!containerFilePath) return;

    try {
      const { data: fileLink } = await campaignsApi(authToken).get<string>('GetUploadedFileLink', {
        filePath: containerFilePath,
      });

      if (fileLink) {
        openFileInNewTab(windowSize === Breakpoints.Desktop, fileLink);
      }
    } catch (err: any) {
      console.error(err);
      dispatch(setAndShowErrorToast(err.message));
      return err.message;
    }
  };

  const getPoolCertComponent = () => {
    if (!poolDocumentationType) return undefined;

    if (poolCertLoadingState === LoadingState.Loading) {
      return [<React.Fragment key="loading">Loading...</React.Fragment>];
    } else if (
      poolDocumentationType === PoolDocumentationType.PoolSafetyCertificate ||
      poolDocumentationType === PoolDocumentationType.BuildingCertificate
    ) {
      const icon =
        certificateExpiresInDays && certificateExpiresInDays < expiryWarningThreshold ? (
          <AlertIcon className="agx-pool-icon" />
        ) : (
          <CheckmarkIcon className="agx-pool-icon" />
        );

      const label = certificateExpiresInDays !== undefined && (
        <AgxBodyText medium>
          {poolDocumentationType === PoolDocumentationType.PoolSafetyCertificate
            ? 'Pool'
            : 'Building'}{' '}
          certificate will expire in {certificateExpiresInDays} days
        </AgxBodyText>
      );

      return [
        <AgxRow fill largeGap key="viewPoolCert">
          {icon}
          <AgxColumn fill largeGap extraClasses="viewPoolCert">
            {label}
            {poolDocumentationType === PoolDocumentationType.PoolSafetyCertificate && (
              <AgxButton
                id="viewPoolCertificate"
                text="View pool certificate"
                primary
                small
                onClick={(): void => {
                  downloadDocument();
                }}
              />
            )}
          </AgxColumn>
        </AgxRow>,
      ];
    } else if (poolDocumentationType === PoolDocumentationType.NoPoolCertificate) {
      return [
        <AgxColumn fill extraLargeGap key="noCert">
          <AgxRow largeGap centered>
            <AlertIcon className="agx-pool-alertIcon" />
            <AgxBodyText medium>No Pool Certificate found</AgxBodyText>
          </AgxRow>
          {selectedForm?.formType !== FormType.ListingSubmission && (
            <AgxBodyText medium>Form 36 Notice of No Pool Certificate will generate</AgxBodyText>
          )}
        </AgxColumn>,
      ];
    }
  };

  return (
    <div className="agx-radio">
      <label className="agx-radio-label">Pool Safety</label>
      <div className="agx-radio-group">
        <AgxRadio
          id="hasPool"
          label={poolLabel}
          onCheckChanged={() => onHasPoolOptionChange(true)}
          checked={hasPool === true}
          subContent={getPoolCertComponent()}
        />
        <AgxRadio
          id="noPool"
          label="No Pool"
          onCheckChanged={() => onHasPoolOptionChange(false)}
          checked={hasPool === false}
        />
      </div>
      {validate && required && hasPool === undefined && (
        <div className="errorMessage">
          <Images.AlertCircle />
          <AgxBodyText small extraClasses="error" dataTestId={`error-${id}`}>
            {'Pool Safety selection required'}
          </AgxBodyText>
        </div>
      )}
    </div>
  );
};

export default PoolSafetyCertificate;
